<script lang="ts">
    import { page } from '$app/stores';
    import { getBlockRevealContext } from '$lib/Stores/blockReveal';
    import { isFirstPageLoad } from '$lib/Stores/stores';

    import { onMount } from 'svelte';
    import { get } from 'svelte/store';

    import { getTransitionData } from './helper';

    import type { TransitionEffect } from '$lib/types/Theme';
    import type { TransitionConfig } from 'svelte/transition';

    export let delay = 0;
    export let effect: TransitionEffect = 'default';
    export let fullHeight = false;

    let ready = false;

    onMount(() => (ready = true));

    // Optionally skip the staggered block entrance on the initial cold load when the
    // funnel opts in via `theme.disableAnimationOnPageLoad`. The per-index delay would
    // otherwise defer the LCP element even when the main thread and network are idle
    // (FUN-3928). Off by default, so live funnels keep their current entrance animation.
    // In-funnel navigation is unaffected — `isFirstPageLoad` flips on the first
    // client-side navigation. Suppression reuses the existing `none` effect (no delay,
    // zero duration), so blocks paint immediately at full opacity.
    $: skipEntrance = $isFirstPageLoad && !!$page?.data?.theme?.disableAnimationOnPageLoad;

    // CAP-4418: A block that sits after an AI embed whose height is not known yet
    // is rendered but not painted, so its entrance would play against something
    // nobody can see and be over well before the block is revealed — leaving it
    // to appear abruptly. Svelte's own transition only runs on insert, and the
    // block cannot wait to be inserted (embeds have to load and measure while
    // held), so for those blocks the entrance is suppressed here and played by
    // hand below, from the same transition function, once the block is revealed.
    const isBlockRevealed = getBlockRevealContext();
    const isHeldAtMount = !get(isBlockRevealed);

    $: activeEffect = skipEntrance || isHeldAtMount ? 'none' : effect;
    $: ({ transitionFunction, transitionProps } = getTransitionData(activeEffect));

    let node: HTMLDivElement | undefined = undefined;
    let heldTransition: TransitionConfig | null = null;
    let hasPlayedHeldEntrance = false;

    /** How finely the transition's own `css` output is sampled into keyframes. */
    const ENTRANCE_KEYFRAME_STEPS = 30;

    /** `transform: translate(0px, 10px); opacity: 0` -> a Web Animations keyframe. */
    const toKeyframe = (declarations: string): Record<string, string> =>
        Object.fromEntries(
            declarations
                .split(';')
                .map((declaration) => declaration.trim())
                .filter(Boolean)
                .map((declaration) => {
                    const separator = declaration.indexOf(':');
                    const property = declaration
                        .slice(0, separator)
                        .trim()
                        .replace(/-([a-z])/g, (_, character: string) => character.toUpperCase());

                    return [property, declaration.slice(separator + 1).trim()];
                }),
        );

    /**
     * Put the block at the start of its entrance, so the reveal has something to
     * animate from. Resolved once per block: `fly` and friends read the element's
     * current opacity and transform when they are called.
     */
    const holdEntrance = (target: HTMLDivElement) => {
        if (heldTransition) {
            return;
        }

        const { transitionFunction: fn, transitionProps: props } = getTransitionData(effect);

        heldTransition = fn(target, props) as TransitionConfig;

        if (heldTransition.css) {
            target.style.cssText = heldTransition.css(0, 1);
        }
    };

    /**
     * Play the held block's entrance, sampling the very transition the theme
     * configured so a held block enters exactly like an unheld one. Handing the
     * keyframes to the browser rather than stepping them on a timer keeps the
     * animation off the main thread, which is busy at exactly this moment.
     */
    const playHeldEntrance = (target: HTMLDivElement) => {
        hasPlayedHeldEntrance = true;

        const transition = heldTransition;
        const duration = transition?.duration ?? 0;

        if (skipEntrance || !transition?.css || duration <= 0) {
            target.style.cssText = '';

            return;
        }

        const easing = transition.easing ?? ((progress: number) => progress);
        const keyframes: Record<string, string>[] = [];

        for (let step = 0; step <= ENTRANCE_KEYFRAME_STEPS; step += 1) {
            const eased = easing(step / ENTRANCE_KEYFRAME_STEPS);

            keyframes.push(toKeyframe(transition.css(eased, 1 - eased)));
        }

        const animation = target.animate(keyframes, {
            duration,
            delay: delay + (transition.delay ?? 0),
            // The easing is already baked into the sampled keyframes.
            easing: 'linear',
            fill: 'backwards',
        });

        animation.onfinish = () => {
            target.style.cssText = '';
        };
    };

    $: if (isHeldAtMount && node && !hasPlayedHeldEntrance) {
        if ($isBlockRevealed) {
            playHeldEntrance(node);
        } else {
            holdEntrance(node);
        }
    }
</script>

{#if ready}
    <div
        bind:this={node}
        class:fun-h-full={fullHeight}
        in:transitionFunction={{ ...transitionProps, delay: activeEffect === 'none' ? 0 : delay }}
    >
        <slot />
    </div>
{/if}
